import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export function Accordion({
  title,
  meta,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
  className,
}: {
  title: ReactNode;
  meta?: ReactNode;
  defaultOpen?: boolean;
  /** Controlled mode: the caller owns the open state (e.g. to keep it across tab switches,
   *  which unmount this component). Without it the accordion keeps its own state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
}) {
  const [inner, setInner] = useState(defaultOpen);
  const open = openProp ?? inner;
  const toggle = () => {
    if (openProp === undefined) setInner(!open);
    onOpenChange?.(!open);
  };
  return (
    <div className={cn('rounded-md border border-(--color-border) bg-(--color-surface) overflow-hidden', className)}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-(--color-bg) transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          <ChevronRight
            size={12}
            className={cn('shrink-0 transition-transform text-(--color-text3)', open && 'rotate-90')}
          />
          <span className="text-[0.769em] font-bold uppercase tracking-wider text-(--color-text2) truncate">{title}</span>
        </span>
        {meta && <span className="text-[0.692em] font-mono text-(--color-text3) shrink-0">{meta}</span>}
      </button>
      {open && (
        <div className="px-3 py-2 border-t border-(--color-border-lite)">{children}</div>
      )}
    </div>
  );
}
